import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { ApiError } from '@/shared/api-client/workspace';
import { Button } from '@/shared/ui/components/button';
import { useVenueContext } from '@/modules/venue/VenueContext';
import { useMvpDefinitions, useSetMvpDefinitionActive, type MvpDefinition } from './definitions';

/** The Multi-Visit Passes tab of Catalog › Products: one row per Definition, with its options on sale. */
export function MvpDefinitionsTable({ canManage }: { canManage: boolean }) {
  const { t } = useTranslation('common');
  const { venueId } = useVenueContext();
  const definitions = useMvpDefinitions(venueId);

  if (definitions.isLoading)
    return <p className="text-muted-foreground">{t('multiVisitPasses.loading')}</p>;

  const items = definitions.data?.items ?? [];

  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead className="border-b bg-muted/50 text-left">
          <tr>
            <th className="px-4 py-2 font-medium">{t('multiVisitPasses.columns.name')}</th>
            <th className="px-4 py-2 font-medium">{t('multiVisitPasses.columns.optionsOnSale')}</th>
            <th className="px-4 py-2 font-medium">{t('multiVisitPasses.columns.waiver')}</th>
            <th className="px-4 py-2 font-medium">{t('multiVisitPasses.columns.status')}</th>
            <th className="px-4 py-2 text-right font-medium">
              {t('multiVisitPasses.columns.actions')}
            </th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr>
              <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                {t('multiVisitPasses.empty')}
              </td>
            </tr>
          )}
          {items.map((definition) => (
            <DefinitionRow key={definition.id} definition={definition} canManage={canManage} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DefinitionRow({
  definition,
  canManage,
}: {
  definition: MvpDefinition;
  canManage: boolean;
}) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { venueId } = useVenueContext();
  const setActive = useSetMvpDefinitionActive(venueId);

  const onSale = definition.active
    ? definition.options
        .filter((option) => option.active)
        .map((option) => `${option.name} ${option.price} ${option.currency}`)
        .join(', ')
    : '';

  async function toggleActive() {
    try {
      await setActive.mutateAsync({ definitionId: definition.id, active: !definition.active });
      toast.success(
        t(
          definition.active
            ? 'multiVisitPasses.toast.deactivated'
            : 'multiVisitPasses.toast.reactivated',
        ),
      );
    } catch (caught) {
      const code = caught instanceof ApiError ? caught.code : undefined;
      const key = code ? `multiVisitPasses.errors.${code}` : 'multiVisitPasses.errors.generic';
      const translated = t(key);
      toast.error(translated === key ? t('multiVisitPasses.errors.generic') : translated);
    }
  }

  return (
    <tr className="border-b last:border-0">
      <td className="px-4 py-2 font-medium">{definition.name}</td>
      <td className="px-4 py-2 text-muted-foreground">{onSale || '—'}</td>
      <td className="px-4 py-2">
        {definition.waiverRequired ? t('multiVisitPasses.waiverRequired') : '—'}
      </td>
      <td className="px-4 py-2">
        {definition.active
          ? t('multiVisitPasses.status.active')
          : t('multiVisitPasses.status.inactive')}
      </td>
      <td className="px-4 py-2">
        <div className="flex items-center justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => navigate(`/loc/${venueId}/products/multi-visit-passes/${definition.id}`)}
          >
            {canManage ? t('multiVisitPasses.actions.edit') : t('multiVisitPasses.actions.view')}
          </Button>
          {canManage && (
            <Button size="sm" variant="ghost" disabled={setActive.isPending} onClick={toggleActive}>
              {definition.active
                ? t('multiVisitPasses.actions.deactivate')
                : t('multiVisitPasses.actions.reactivate')}
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}
